import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { QRCodeCanvas } from 'qrcode.react';
import { ArrowDownToLine, Copy, ShieldCheck, AlertTriangle, Radio } from 'lucide-react';
import api from '@/lib/api';

// Deposit flow — two-mode:
//   HD MODE (chain has a real blockchain provider like Alchemy/BlockCypher):
//     Address is derived from BITGONODE's HD master seed → unique per user.
//     Deposits are auto-detected via the provider webhook + reconciled by the
//     background monitor. NO tx-hash entry required.
//   POOL MODE (chain uses SimulationProvider or has no HD config):
//     Legacy admin-curated pool address. User submits {tx_hash, amount} and
//     admin approves. Kept for coins we haven't cut over to HD yet.
export default function DepositDialog({ open, onOpenChange }) {
  const [networks, setNetworks] = useState({});
  const [symbol, setSymbol] = useState('USDT');
  const [network, setNetwork] = useState('TRC20');
  const [addr, setAddr] = useState(null);        // {address, memo_or_tag, ...} or null
  const [status, setStatus] = useState('idle');  // 'idle'|'loading'|'no_address'|'ready'
  const [errMsg, setErrMsg] = useState('');
  const [mode, setMode] = useState('pool');      // 'hd' | 'pool' — chosen per-chain
  const [hdChains, setHdChains] = useState({});  // { SYMBOL: chain_row }
  // Claim step (only used in POOL mode)
  const [txHash, setTxHash] = useState('');
  const [amount, setAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [mine, setMine] = useState([]);
  const [hdDeposits, setHdDeposits] = useState([]);

  const loadNetworks = async () => {
    try {
      const r = await api.get('/wallet/networks');
      setNetworks(r.data.supported || {});
    } catch (_e) { setNetworks({}); }
  };
  const loadHdChains = async () => {
    try {
      const r = await api.get('/hd/chains');
      const map = {};
      (r.data.chains || []).forEach(c => { map[c.symbol.toUpperCase()] = c; });
      setHdChains(map);
    } catch (_e) { setHdChains({}); }
  };
  const loadMine = async () => {
    try {
      const [pool, hd] = await Promise.all([
        api.get('/wallet/deposits/mine?limit=10').catch(() => ({ data: { requests: [] } })),
        api.get('/hd/deposits/my').catch(() => ({ data: { deposits: [] } })),
      ]);
      setMine(pool.data.requests || []);
      setHdDeposits((hd.data.deposits || []).slice(0, 10));
    } catch (_e) { /* silent */ }
  };
  useEffect(() => { if (open) { loadNetworks(); loadHdChains(); loadMine(); } }, [open]);

  // Chain uses "hd" mode when a real (non-simulation) HD provider is configured for it.
  // USDT is a token — resolve to its underlying chain by network (TRC20→TRX, ERC20→ETH, BEP20→BNB).
  const underlyingChainForToken = (sym, net) => {
    if (sym !== 'USDT') return sym;
    if (net === 'TRC20') return 'TRX';
    if (net === 'ERC20') return 'ETH';
    if (net === 'BEP20') return 'BNB';
    return sym;
  };
  const chainMode = (sym, net = network) => {
    const target = underlyingChainForToken(sym?.toUpperCase(), net);
    const c = hdChains[target?.toUpperCase()];
    if (!c) return 'pool';
    return (c.provider_type && c.provider_type !== 'simulation') ? 'hd' : 'pool';
  };

  const fetchAddress = async () => {
    setStatus('loading'); setErrMsg(''); setAddr(null);
    const useHd = chainMode(symbol, network) === 'hd';
    setMode(useHd ? 'hd' : 'pool');
    try {
      if (useHd) {
        // For USDT (a token), fetch the underlying chain's address (same address, tracked as token)
        const chainSymbol = underlyingChainForToken(symbol.toUpperCase(), network);
        const r = await api.get(`/hd/deposit-address/${chainSymbol}`);
        setAddr({ address: r.data.address, network: r.data.network || 'mainnet',
                  min_deposit: r.data.min_deposit, required_confirmations: r.data.required_confirmations });
      } else {
        const r = await api.get(`/wallet/deposit_address?symbol=${symbol}&network=${network}`);
        setAddr(r.data);
      }
      setStatus('ready');
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === 'object' && d.no_address) {
        setStatus('no_address');
        setErrMsg(d.message || 'No address available yet');
      } else {
        setStatus('idle');
        setErrMsg((typeof d === 'string' ? d : null) || 'Failed to fetch address');
      }
    }
  };
  useEffect(() => {
    if (!open) return;
    if (chainMode(symbol, network) === 'hd') { fetchAddress(); return; }
    if (symbol && network && networks[symbol]?.includes(network)) fetchAddress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, symbol, network, networks, hdChains]);

  // When symbol changes, reset network to the first valid one for that symbol (POOL mode only)
  useEffect(() => {
    const nets = networks[symbol];
    if (nets && nets.length > 0 && !nets.includes(network)) setNetwork(nets[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, networks]);

  const copyAddr = () => {
    if (!addr?.address) return;
    navigator.clipboard.writeText(addr.address);
    toast.success('Address copied');
  };

  const submitClaim = async () => {
    if (!addr?.address) return;
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error('Enter the amount you sent'); return; }
    if (!txHash.trim()) { toast.error('Enter the transaction hash'); return; }
    setSubmitting(true);
    try {
      await api.post('/wallet/deposits/submit', {
        symbol, network, amount: amt, tx_hash: txHash.trim(),
      });
      toast.success('Deposit submitted — awaiting admin approval');
      setTxHash(''); setAmount('');
      loadMine();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submission failed');
    } finally { setSubmitting(false); }
  };

  const symbols = Array.from(new Set([...Object.keys(networks), ...Object.keys(hdChains)])).sort();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className="bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] font-semibold rounded-sm" data-testid="open-deposit-btn">
          <ArrowDownToLine className="w-4 h-4 mr-1.5" /> Deposit
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#181A20] border-[#2B3139] text-white rounded-sm max-w-md max-h-[92vh] overflow-y-auto p-4 gap-3" data-testid="deposit-dialog">
        <DialogHeader className="pb-1">
          <DialogTitle className="flex items-center gap-2 text-base">
            <ArrowDownToLine className="w-4 h-4 text-[#FCD535]" /> Deposit
          </DialogTitle>
        </DialogHeader>

        {/* Symbol + Network selector */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <div className="text-[10px] uppercase font-mono text-[#848E9C] mb-1">Coin</div>
            <div className="flex flex-wrap gap-1">
              {symbols.map(s => {
                // For USDT we can't know without a network yet — show green if ANY network is HD.
                const isHd = s === 'USDT'
                  ? ['TRC20','ERC20','BEP20'].some(n => chainMode(s, n) === 'hd')
                  : chainMode(s) === 'hd';
                return (
                  <button key={s} onClick={() => setSymbol(s)}
                    className={`text-xs uppercase font-mono px-2 py-1 border rounded-sm relative ${symbol === s ? 'bg-[#FCD535] text-[#0B0E11] border-[#FCD535]' : 'border-[#2B3139] text-[#B7BDC6] hover:border-[#FCD535]'}`}
                    data-testid={`deposit-symbol-${s}`}>
                    {s}
                    {isHd && <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-[#0ECB81]" title="Auto-detected via HD Wallet" />}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase font-mono text-[#848E9C] mb-1">Network</div>
            {mode === 'hd' ? (
              <div className="text-xs uppercase font-mono px-2 py-1 border border-[#0ECB81]/40 bg-[#0ECB81]/10 rounded-sm text-[#0ECB81] inline-flex items-center gap-1.5" data-testid="deposit-network-hd">
                <Radio className="w-3 h-3" /> AUTO ({addr?.network || 'mainnet'})
              </div>
            ) : (
              <div className="flex flex-wrap gap-1">
                {(networks[symbol] || []).map(n => (
                  <button key={n} onClick={() => setNetwork(n)}
                    className={`text-xs uppercase font-mono px-2 py-1 border rounded-sm ${network === n ? 'bg-[#FCD535] text-[#0B0E11] border-[#FCD535]' : 'border-[#2B3139] text-[#B7BDC6] hover:border-[#FCD535]'}`}
                    data-testid={`deposit-network-${n}`}>{n}</button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Mode banner — clarifies whether the deposit will be auto-detected or admin-approved */}
        {mode === 'hd' && status === 'ready' && (
          <div className="border border-[#0ECB81]/40 bg-[#0ECB81]/10 p-2 rounded-sm text-[11px] text-[#0ECB81] flex items-start gap-2" data-testid="deposit-hd-banner">
            <Radio className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>Auto-detected deposit — send funds to the address below and your balance will credit automatically after <strong>{addr?.required_confirmations || 12}</strong> confirmations. No tx-hash entry, no admin approval needed.</span>
          </div>
        )}

        {/* Address block */}
        {status === 'loading' && (
          <div className="border border-[#2B3139] bg-[#0B0E11] p-4 text-center text-[#848E9C] text-sm">Fetching address…</div>
        )}
        {status === 'no_address' && (
          <div className="border border-[#F6465D]/40 bg-[#F6465D]/10 p-4 text-sm" data-testid="deposit-no-address">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-[#F6465D] flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-[#F6465D] font-semibold">No {symbol} ({network}) address available yet</div>
                <div className="text-[#B7BDC6] text-xs mt-1">
                  Please contact support — an administrator needs to assign you a deposit address on this network before you can deposit.
                </div>
              </div>
            </div>
          </div>
        )}
        {status === 'ready' && addr && (
          <div className="border border-[#2B3139] bg-[#0B0E11] p-3 space-y-2.5" data-testid="deposit-address-block">
            <div className="grid place-items-center bg-white p-2 rounded-sm w-fit mx-auto">
              <QRCodeCanvas value={addr.address} size={124} level="M" data-testid="deposit-qr" />
            </div>
            <div>
              <div className="text-[10px] uppercase font-mono text-[#848E9C]">{symbol} · {network} address</div>
              <div className="flex items-center gap-2 mt-1">
                <code className="flex-1 font-mono text-xs text-white break-all bg-[#181A20] border border-[#2B3139] p-2 rounded-sm select-all" data-testid="deposit-address-text">
                  {addr.address}
                </code>
                <button onClick={copyAddr} className="p-2 border border-[#2B3139] hover:border-[#FCD535] rounded-sm" data-testid="deposit-copy-address" aria-label="Copy address">
                  <Copy className="w-3.5 h-3.5 text-[#FCD535]" />
                </button>
              </div>
            </div>
            {addr.memo_or_tag && (
              <div className="border border-[#F0B90B]/40 bg-[#F0B90B]/5 p-2 rounded-sm text-xs">
                <span className="text-[#F0B90B] font-semibold">Memo / Tag required:</span>
                <code className="ml-2 font-mono text-white">{addr.memo_or_tag}</code>
              </div>
            )}
            <div className="border border-[#F0B90B]/30 bg-[#F0B90B]/5 p-2 rounded-sm text-[11px] text-[#F0B90B] flex items-start gap-2">
              <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>Send only <strong>{symbol}</strong> on the <strong>{network}</strong> network. Sending other assets or wrong network will result in permanent loss.</span>
            </div>
            {mode === 'hd' ? (
              /* HD mode — no tx-hash entry needed. Just show a helpful checklist. */
              <div className="pt-2 border-t border-[#2B3139] text-[11px] text-[#B7BDC6] space-y-1" data-testid="deposit-hd-checklist">
                <div className="text-[10px] uppercase font-mono text-[#848E9C] mb-1">What happens next</div>
                <div>1. Send <span className="font-semibold text-white">{symbol}</span> from any wallet or exchange to the address above.</div>
                <div>2. Wait for <span className="font-mono text-[#FCD535]">{addr?.required_confirmations || 12}</span> network confirmations (~2–5 minutes for ETH).</div>
                <div>3. Your balance credits automatically. No action needed.</div>
              </div>
            ) : (
              /* POOL mode — legacy manual claim */
              <div className="pt-2 border-t border-[#2B3139]">
                <div className="text-[10px] uppercase font-mono text-[#848E9C] mb-2">Confirm your deposit</div>
                <div className="space-y-2">
                  <Input placeholder="Amount sent" value={amount} onChange={e => setAmount(e.target.value)} type="number"
                    className="bg-[#181A20] border-[#2B3139] rounded-sm text-white font-mono"
                    data-testid="deposit-amount-input" />
                  <Input placeholder="Transaction hash (from your wallet)" value={txHash} onChange={e => setTxHash(e.target.value)}
                    className="bg-[#181A20] border-[#2B3139] rounded-sm text-white font-mono"
                    data-testid="deposit-txhash-input" />
                  <Button onClick={submitClaim} disabled={submitting || !amount || !txHash}
                    className="w-full bg-[#FCD535] hover:bg-[#F0B90B] disabled:opacity-50 text-[#0B0E11] rounded-sm font-semibold"
                    data-testid="deposit-submit-btn">
                    {submitting ? 'Submitting…' : 'Submit for Admin Approval'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
        {errMsg && status === 'idle' && (
          <div className="border border-[#F6465D]/40 bg-[#F6465D]/10 p-2 text-xs text-[#F6465D]">{errMsg}</div>
        )}

        {/* My recent deposit requests (both HD auto-detected and pool manual) */}
        {(hdDeposits.length > 0 || mine.length > 0) && (
          <div className="border border-[#2B3139] bg-[#181A20] p-3" data-testid="deposit-mine-list">
            <div className="text-[10px] uppercase font-mono text-[#848E9C] mb-2">Recent deposits</div>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {hdDeposits.map(d => (
                <div key={d.deposit_id || d.tx_hash} className="flex items-center justify-between text-xs py-1 border-b border-[#2B3139]/40 last:border-b-0" data-testid={`hd-deposit-${d.deposit_id || d.tx_hash}`}>
                  <div className="min-w-0">
                    <div className="text-white font-mono">
                      {d.amount} {d.symbol}
                      <span className="text-[#0ECB81] ml-2 text-[10px]" title="Auto-detected via HD Wallet">● AUTO</span>
                    </div>
                    <div className="text-[10px] font-mono text-[#5E6673] truncate max-w-[220px]">{d.tx_hash}</div>
                  </div>
                  <span className={`text-[10px] uppercase font-mono px-2 py-0.5 rounded-sm ${
                    d.status === 'confirmed' || d.status === 'credited' ? 'bg-[#0ECB81]/20 text-[#0ECB81]' :
                    d.status === 'failed' ? 'bg-[#F6465D]/20 text-[#F6465D]' :
                    'bg-[#F0B90B]/20 text-[#F0B90B]'
                  }`}>
                    {d.status}{d.confirmations != null && d.status !== 'confirmed' && d.status !== 'credited' ? ` · ${d.confirmations}` : ''}
                  </span>
                </div>
              ))}
              {mine.map(r => (
                <div key={r.request_id} className="flex items-center justify-between text-xs py-1 border-b border-[#2B3139]/40 last:border-b-0">
                  <div>
                    <div className="text-white font-mono">{r.amount} {r.symbol} <span className="text-[#5E6673]">· {r.network}</span></div>
                    <div className="text-[10px] font-mono text-[#5E6673] truncate max-w-[220px]">{r.tx_hash}</div>
                  </div>
                  <span className={`text-[10px] uppercase font-mono px-2 py-0.5 rounded-sm ${
                    r.status === 'approved' ? 'bg-[#0ECB81]/20 text-[#0ECB81]' :
                    r.status === 'rejected' ? 'bg-[#F6465D]/20 text-[#F6465D]' :
                    'bg-[#F0B90B]/20 text-[#F0B90B]'
                  }`} data-testid={`deposit-mine-status-${r.request_id}`}>
                    {r.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
